import React, { useState, useRef } from "react";
import { FileCheck2, Loader2, Check, AlertTriangle } from "lucide-react";
import { useI18n } from "i18n";
import api from "lib/api";

// Upload a national document (PDF/image) to verify the owner and promote the biometric to
// the GOLD tier. `endpoint` is the API path; `field`/`extra` shape the multipart body.
// Reasons the backend can return: not_authentic, sandbox_document, expired_too_long,
// no_expiry, parse_failed, library_unavailable, empty.
const REASON_KEY = {
  not_authentic: "doc.reason.notAuthentic",
  sandbox_document: "doc.reason.sandbox",
  expired_too_long: "doc.reason.expired",
  no_expiry: "doc.reason.noExpiry",
  parse_failed: "doc.reason.parseFailed",
  library_unavailable: "doc.reason.unavailable",
  empty: "doc.reason.empty",
  face_mismatch: "doc.reason.faceMismatch",
  photo_unavailable: "doc.reason.photoUnavailable",
  doc_no_face: "doc.reason.docNoFace",
  no_enrollment: "doc.reason.noEnrollment",
  review_pending: "doc.reason.reviewPending",
};

export default function FaceDocumentUpload({ endpoint, extra = {}, onGold, compact = false }) {
  const { t } = useI18n();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // {ok, reason, meta}

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("document", file);
      Object.entries(extra).forEach(([k, v]) => v != null && fd.append(k, v));
      const r = await api.post(endpoint, fd);
      setResult({ ok: true, ...r.data });
      onGold && onGold();
    } catch (e) {
      const d = e?.response?.data || {};
      setResult({ ok: false, reason: d.reason || "parse_failed", meta: d.meta });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])} />
      <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
        className="inline-flex items-center gap-2 h-10 px-3.5 rounded-lg border border-amber-500/40 text-amber-500 text-sm hover:bg-amber-500/10 transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileCheck2 className="h-4 w-4" />}
        {t("doc.upload", "Enviar um documento...")}
      </button>
      {!compact && (
        <p className="text-xs text-muted-foreground mt-1">
          {t("doc.hint", "Envie a CNH/documento digital (PDF ou imagem do QR). Verificação offline; aceita documentos autênticos vencidos há no máximo 6 meses.")}
        </p>
      )}
      {result && (
        <div className={`mt-2 text-sm inline-flex items-center gap-1.5 ${result.ok ? "text-emerald-500" : "text-red-500"}`}>
          {result.ok ? <Check className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          {result.ok
            ? t("doc.gold", "Documento verificado — nível Ouro concedido.")
            : t(REASON_KEY[result.reason] || "doc.reason.parseFailed", "Documento inválido.")}
        </div>
      )}
    </div>
  );
}
